import { CloseCircleOutlined, DragOutlined, RightCircleFilled } from "@ant-design/icons";
import { useABContext } from "@src/contexts/ab-testing/ab-context";
import { ChartFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { Button, Form, Input } from "antd";
import { MenuProps } from "antd/lib/menu";
import React from "react";
import { EventStepDropdown } from "./event-step-dropdown";
import { EventListDataType, removeEvents } from "@src/util/ab-testing/funnel/manage-state-event-lists";
import { CSS } from "@dnd-kit/utilities";
import { rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { ApplyButton } from "./apply-button";
import { FormInstance } from "antd/lib/form";
import { ApplyValueStateType } from ".";

const MAX_EVENT_STEP = 10;
export const EventStep: React.FC<{
  form: FormInstance<any>;
  handleApply: (values: ApplyValueStateType) => void;
  applyValue: ApplyValueStateType;
}> = ({ form, applyValue, handleApply }) => {
  const [errMessage, setErrMessage] = React.useState<string>();
  const { listEventNames, chartForm } = useABContext();
  const eventNameListWatch: EventListDataType[] =
    Form.useWatch(ChartFormFieldsEnum.FUNNEL_EVENT_LISTS, chartForm) ?? [];

  const dropdownItem: MenuProps["items"] = React.useMemo(() => {
    return listEventNames
      .filter((item) => !eventNameListWatch.some((eventName) => item?.event_name === eventName.name))
      ?.map((item) => {
        return {
          key: item?.event_name,
          title: item?.event_name,
          label: item?.event_name,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listEventNames, JSON.stringify(eventNameListWatch)]);

  const updateField = (newValue: EventListDataType[]) => {
    chartForm.setFieldValue(ChartFormFieldsEnum.FUNNEL_EVENT_LISTS, newValue);
    chartForm.validateFields([ChartFormFieldsEnum.FUNNEL_EVENT_LISTS]);
  };

  const handleRemoveEvent = (name: string) => {
    updateField(removeEvents(chartForm.getFieldValue(ChartFormFieldsEnum.FUNNEL_EVENT_LISTS), name));
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const sortedItems = [...eventNameListWatch].sort((a, b) => a.order - b.order);

  const isDisabledAddEvent = eventNameListWatch.length >= MAX_EVENT_STEP;
  return (
    <div className="w-full">
      <div className="flex justify-between items-start w-full">
        <div className="flex ">
          <Form.Item label="Select Event Step" required className="m-0">
            <Form.Item
              hidden
              className="m-0"
              name={ChartFormFieldsEnum.FUNNEL_EVENT_LISTS + "_validate_field"}
              rules={[
                {
                  validator: (_) => {
                    if (eventNameListWatch?.length > 0) {
                      if (eventNameListWatch?.length > MAX_EVENT_STEP) {
                        setErrMessage(`Event name must <= ${MAX_EVENT_STEP}!`);
                        return Promise.reject(`Event name must <= ${MAX_EVENT_STEP}!`);
                      }
                      setErrMessage(undefined);
                      return Promise.resolve();
                    }
                    setErrMessage("Event name is required!");
                    return Promise.reject("Event name is required!");
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Form.Item>
          <EventStepDropdown
            dropdownItem={dropdownItem}
            form={chartForm}
            isDisabled={isDisabledAddEvent}
            updateField={updateField}
          />
          {eventNameListWatch.length > 0 && (
            <Button
              danger
              type="link"
              onClick={() => {
                chartForm.setFieldValue(ChartFormFieldsEnum.FUNNEL_EVENT_LISTS, []);
              }}
            >
              Clear All
            </Button>
          )}
        </div>
        <ApplyButton form={form} applyValue={applyValue} handleApply={handleApply} />
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id) return;

          const oldIndex = sortedItems.findIndex((item) => item.name === active.id);
          const newIndex = sortedItems.findIndex((item) => item.name === over.id);

          const newSorted = [...sortedItems];
          const [movedItem] = newSorted.splice(oldIndex, 1);
          newSorted.splice(newIndex, 0, movedItem);

          const reordered = newSorted.map((item, index) => ({
            ...item,
            order: index,
          }));

          updateField(reordered);
        }}
      >
        <SortableContext items={sortedItems.map((item) => item.name)} strategy={rectSortingStrategy}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              padding: "10px",
            }}
          >
            {sortedItems.map((item) => (
              <SortableItem
                key={item.name}
                item={item}
                handleRemoveEvent={handleRemoveEvent}
                listLength={sortedItems.length}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {errMessage && <span className="text-red-500">{errMessage}</span>}
    </div>
  );
};
const SortableItem = ({
  item,
  handleRemoveEvent,
  listLength,
}: {
  item: EventListDataType;
  handleRemoveEvent: (name: string) => void;
  listLength: number;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, index } = useSortable({
    id: item.name,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: isDragging ? 50 : 0,
    cursor: "default",
  };
  return (
    <div ref={setNodeRef} {...attributes} style={style} className="flex items-center">
      <div className="flex items-center mr-2 my-2 bg-gray-300/30 rounded-[10px] border-[1px] border-gray-300 border-solid hover:bg-gray-300/60 duration-100">
        <Button type="primary" icon={<DragOutlined />} {...listeners} />

        <span className="flex-1 px-4 ">{item.name}</span>
        <CloseCircleOutlined
          className="text-red-500 mr-2 pr-1 text-[14px] hover:text-[18px] hover:pr-0 duration-100"
          onClick={() => {
            handleRemoveEvent(item.name);
          }}
        />
      </div>
      {index < listLength - 1 ? <RightCircleFilled className="mr-2" /> : null}
    </div>
  );
};
